import React, {
  createContext,
  useState,
  useRef,
  useCallback,
  PropsWithChildren,
} from 'react'
import { FetchPlaylistResponse } from '../../swagger'
import { playlistApi } from '../../api/swagger'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { getRedirectPathFrom404 } from '../../utils/playlistRedirect'
import { useLocation, useNavigate } from 'react-router'

/* -------------------------------------------------------------------------------------------------
 * PlaylistContext
 * Context to manage the state and fetching logic for a playlist based on its playlistKey.
 * -------------------------------------------------------------------------------------------------
 * This context provides:
 * - The current playlist (`playlist`)
 * - Loading state (`isLoading`)
 * - Functions to load, refetch and manually update the playlist
 *
 * Must be wrapped with <PlaylistProvider> to function correctly.
 * ------------------------------------------------------------------------------------------------- */

/* ---------- Interfaces ---------- */
interface PlaylistContextType {
  playlist?: FetchPlaylistResponse
  isLoading: boolean
  loadPlaylist: (playlistKey: number) => void
  refetch: () => void
  updatePlaylist: (value: FetchPlaylistResponse) => void
}

export const defaultContextValue: PlaylistContextType = {
  playlist: undefined,
  isLoading: false,
  loadPlaylist: async (playlistKey: number) => {
    console.warn(
      `loadPlaylist was called with playlistKey=${playlistKey}, but PlaylistProvider is missing. Did you forget to wrap with <PlaylistProvider>?`
    )
  },
  refetch: async () => {
    console.warn(
      `refetch was called, but PlaylistProvider is missing. Did you forget to wrap with <PlaylistProvider>?`
    )
  },
  updatePlaylist: async () => {
    console.warn(
      `updatePlaylist was called, but PlaylistProvider is missing. Did you forget to wrap with <PlaylistProvider>?`
    )
  },
}

/* ---------- Context Creation ---------- */
export const PlaylistContext =
  createContext<PlaylistContextType>(defaultContextValue)

export type PlaylistConfig = Partial<typeof defaultContextValue>

export interface PlaylistConfigWithProps extends PropsWithChildren {
  testConfig?: PlaylistConfig
}

/* ---------- Provider ---------- */
export const PlaylistProvider: React.FC<PlaylistConfigWithProps> = ({
  testConfig,
  children,
}) => {
  const [playlist, setPlaylist] = useState<FetchPlaylistResponse>()
  const lastPlaylistKeyRef = useRef<number>(0)
  const skipFirstPlaylistItemsEffect = useRef(false)
  const { handleError } = useAppSnackbar()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    Playlist: { fetchPlaylist: fetchPlaylistLoadingId },
  } = useLoadingIds()
  const fetchPlaylist = useCallback(
    async (playlistKey: number) => {
      try {
        const result = await playlistApi.fetchPlaylist({ playlistKey })
        setPlaylist(result)
        skipFirstPlaylistItemsEffect.current = true
      } catch (error) {
        if ((error as { status: number }).status === 404) {
          navigate(getRedirectPathFrom404(location.pathname), { replace: true })
        }
        await handleError(error, 'Error occurred while retrieving playlist')
      }
    },
    [handleError, location.pathname, navigate]
  )

  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: () => fetchPlaylist(lastPlaylistKeyRef.current),
    loadingId: fetchPlaylistLoadingId,
  })

  const loadPlaylist = useCallback(
    (playlistKey: number) => {
      if (playlist?.playlist?.playlistKey === playlistKey) return
      lastPlaylistKeyRef.current = playlistKey
      triggerFetch()
    },
    [playlist, triggerFetch]
  )

  /**
   * Allows manual update of the playlist value in state.
   */
  const updatePlaylist = (value: FetchPlaylistResponse) => {
    setPlaylist(value)
  }

  const value: PlaylistContextType = {
    playlist,
    isLoading,
    loadPlaylist,
    refetch: triggerFetch,
    updatePlaylist,
    ...testConfig,
  }

  return (
    <PlaylistContext.Provider value={value}>
      {children}
    </PlaylistContext.Provider>
  )
}
